<!--
 @description In-game feedback form that lets players report issues or suggestions during
 a game session. Submits a subject + message via the mail service, gated by reCAPTCHA v3
 verification to prevent spam. Disables the send button while verification is in progress.
 Emits `close` after a successful submission so the parent can hide the containing modal.

 Optionally receives the active `game` and `challenge` context so the feedback can be
 linked to a specific moment in the session on the backend.

 @example
 <FeedbackForm :game="currentGame" :challenge="currentChallenge" @close="closeFeedbackModal" />
-->
<template>
  <form class="feedback-form">
    <h2>{{ $t('game.game.feedback.title') }}</h2>
    <p>
      {{ $t('game.game.feedback.text-1') }}
    </p>
    <p class="bold">{{ $t('game.game.feedback.text-3') }}</p>

    <Input
      id="subject"
      class="input"
      :title="$t('global.form.subject')"
      :value="subject"
      @update:modelValue="updateSubject"
      :validation="{ required: true }"
    />
    <TextArea
      id="message"
      class="input"
      :title="$t('global.form.your-message')"
      :rows="10"
      :value="message"
      @update:modelValue="updateMessage"
      :validation="{ required: true }"
    />
    <em>{{ $t('game.game.feedback.text-2') }}</em>
    <button
      class="secondary"
      :disabled="!formIsValid || isVerifying"
      :class="{ disabled: !formIsValid || isVerifying }"
      @click.prevent="send"
    >
      {{ isVerifying ? $t('global.verifying') : $t('global.form.send') }}
    </button>
  </form>
</template>

<script setup lang="ts">
import Input from '@/components/common/form/input/Input.vue'
import TextArea from '@/components/common/form/textarea/TextArea.vue'
import mailService from '@/stores/mail'

import type { Status, Game, Challenge } from '@/types/Types'

import { useI18n } from 'vue-i18n'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { useRecaptchaGuard } from '@/composables/useRecaptchaGuard'

const props = defineProps<{
  /** The current game session. Attached to the feedback record for admin triage. Omit outside a game session. */
  game?: Game
  /** The challenge being played when the feedback was triggered. Allows admins to reproduce the exact context. */
  challenge?: Challenge
}>()

const emits = defineEmits(['close'])

const { t } = useI18n()
const toast = useToast()
const { isVerifying, withRecaptcha } = useRecaptchaGuard()
const subject = ref('')
const subjectStatus = ref<Status>({ valid: false, message: '' })
const message = ref('')
const messageStatus = ref<Status>({ valid: false, message: '' })

function updateSubject(value: string, status: Status) {
  subject.value = value
  subjectStatus.value = status
}

function updateMessage(value: string, status: Status) {
  message.value = value
  messageStatus.value = status
}

const formIsValid = computed(() => subjectStatus.value.valid && messageStatus.value.valid)

async function send() {
  if (!formIsValid.value) return

  const verified = await withRecaptcha('game_feedback', () => {
    mailService.sendFeedback(subject.value, message.value, props.game, props.challenge, toast, t)
  })

  if (verified) {
    clearForm()
    emits('close')
  }
}

function clearForm() {
  subject.value = ''
  subjectStatus.value = { valid: false, message: '' }
  message.value = ''
  messageStatus.value = { valid: false, message: '' }
}
</script>

<style scoped lang="scss">
.feedback-form {
  @extend %flex-column;
  max-width: 600px;
  padding: $double-spacing 0;

  h2,
  p,
  em {
    text-align: center;
    padding: 0 $base-spacing;
  }

  em {
    font-size: 0.8rem;
    margin: 0 auto;
    text-align: center;
    display: block;
  }

  button,
  .input {
    width: calc(100% - $base-spacing) !important;
  }
}
</style>
